/** @module */

/**
 * Holds the actual Blob behind each local `blob:` URL we put into comment text.
 *
 * Why this exists: a `blob:` URL is only a temporary in-memory handle owned by the browser. It can
 * become unusable between the time an image is pasted and the time the comment is saved (document
 * teardown, memory pressure, or the browser reclaiming it). When that happens `fetch(blobUrl)` throws
 * `TypeError: Failed to fetch`, which surfaced to users as
 * "Cannot upload blobs in comment: Failed to fetch".
 *
 * We already have the real Blob at paste time, so we keep it here and upload that instead of
 * re-fetching a handle that may no longer resolve.
 */

/** @type {Map<string, Blob>} */
const blobsByUrl = new Map();

/**
 * Remember the Blob behind a local blob: URL so it can be uploaded later without re-fetching.
 * @param {string} url The `blob:` URL as it appears in the comment text
 * @param {Blob} blob The Blob the URL was created from
 */
export function registerBlob(url, blob) {
	if (!url || !blob) return;
	blobsByUrl.set(url, blob);
}

/**
 * @param {string} url
 * @returns {Blob | undefined} The retained Blob, or undefined if it was never registered
 */
export function getRegisteredBlob(url) {
	return blobsByUrl.get(url);
}

/**
 * Drop a retained Blob once it has been uploaded (or abandoned) so we don't hold memory.
 * @param {string} url
 */
export function releaseBlob(url) {
	blobsByUrl.delete(url);
}

/**
 * Release every retained Blob. Used when tearing down an editor so cancelled edits don't leak.
 * @param {string[]} [urls] Only release these urls. Releases everything when omitted.
 */
export function releaseBlobs(urls) {
	if (!urls) { blobsByUrl.clear(); return; }
	urls.forEach(url => blobsByUrl.delete(url));
}
